---
title: Manage your asset library
summary: Upload, preview, tag, and edit the approved files your AI expert can send to customers.
visibility: authenticated
feature: inbox
audience: author-admin
section: How-to
order: 65
last_verified: 2026-08-08
owner: docs
---

# Manage your asset library

The **Assets** page holds the approved files — images and PDFs like brochures,
menus, and price sheets — that your AI expert is allowed to send to customers,
and that your team can attach from the Inbox. The AI can only ever send files
from this library that its persona has been granted.

## Upload a file

Click **Add Asset** and choose a JPEG, PNG, WebP, or PDF. Give it:

- **Name** — what humans see in lists and pickers.
- **Slug** — the stable handle the AI refers to. It can't be changed after
  upload, so pick something readable like `product-brochure`.
- **Description** (optional) — the AI reads this when deciding which file fits
  a request, so a sentence about what's inside genuinely helps.
- **Tags** (optional) — see below.

## Preview and download

Click any row (or its eye icon) to preview the file — images display inline and
PDFs open in an embedded viewer — along with its slug, type, size, and tags.
The **Download** button in the preview saves the file locally.

## Tag your files

Tags are short keywords like `brochure`, `menu`, or `precios`. They do two jobs:

- **For your team** — the search box on the Assets page and in the Inbox
  file picker matches names, slugs, and tags, so a well-tagged library stays
  findable as it grows.
- **For the AI** — when a customer asks for a brochure or menu, the AI looks
  for a granted file whose slug, name, or **tags** read like one. A PDF with an
  unhelpful name still gets found if it's tagged `brochure` or `menú` — tag in
  the language your customers use.

## Edit a file's details

Use the pencil icon to change an asset's name, description, and tags at any
time. The file's bytes and slug are fixed — to replace the file itself, upload
a new asset.

## Let a persona send an asset

Uploading an asset does **not** automatically let your AI expert send it.
Each persona has its own allowlist: open **Personas → your persona** and find
**Sendable assets** under **What your expert can do**, then either tick
**Can send any of my approved assets** or select individual assets. Until an
asset is granted there, the AI will tell customers it can't share it — even
though your team can still attach it manually from the Inbox.

## Link assets

Besides uploaded documents and images, the library can hold **links** —
approved URLs like your delivery-platform store pages. Click **Add Link**
and give it a name, slug, the `https://` address, and tags in the language
your customers use (for example `uber`, `rappi`, `delivery app`). Granting
works exactly like any other asset: once a persona is allowed to send it,
the AI can share the link as a normal chat message when a customer
explicitly asks for it. Links have no preview or download — the URL itself
is the content.

## See also

- [Manage your product catalog](/help/how-to/manage-your-product-catalog) —
  tagging products, rather than files.
